import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, TrendingUp, TrendingDown, DollarSign, UserMinus, Percent, FlaskConical, Search } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardValue, CardSubtitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { SortableColumn, useTableSort } from '@/components/ui/SortableColumn';
import { api } from '@/lib/api';
import { formatNumber } from '@/lib/utils';

const SANDBOX_TOGGLE_KEY = 'amuzze_admin_pro_show_sandbox';

function formatBrl(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: '2-digit' });
}

// `plan_code` guarda o product_id da loja, não um código nosso: a Apple manda
// `amuzze_pro_monthly` e o Google manda `amuzze_pro_monthly:monthly` (produto +
// plano-base). O mapa antigo comparava com 'pro_monthly' exato e nunca casava —
// a coluna mostrava o código cru, e o mesmo erro no backend zerava o MRR.
// Classificar por padrão aguenta sufixo novo que a loja invente.
function planLabel(code: string | null | undefined): string {
  if (!code) return '—';
  const c = code.toLowerCase();
  if (c.includes('lifetime')) return 'Lifetime';
  if (c.includes('annual') || c.includes('year')) return 'Anual';
  if (c.includes('month')) return 'Mensal';
  if (c.includes('manual')) return 'Manual';
  return code; // desconhecido: mostra cru em vez de mentir
}

const SOURCE_LABEL: Record<string, string> = {
  apple:  'Apple',
  google: 'Google',
  manual: 'Manual',
};

export function Pro() {
  // Toggle sandbox persistido em localStorage. Default OFF pra dashboard
  // limpo (sandbox são assinaturas de teste via Sandbox Tester Apple/Google).
  const [showSandbox, setShowSandbox] = useState<boolean>(
    () => localStorage.getItem(SANDBOX_TOGGLE_KEY) === '1'
  );
  const toggleSandbox = () => {
    const next = !showSandbox;
    setShowSandbox(next);
    localStorage.setItem(SANDBOX_TOGGLE_KEY, next ? '1' : '0');
  };
  const qs = showSandbox ? '?include_sandbox=true' : '';

  // Busca no SERVIDOR, não na lista já carregada: sem `search` o backend só
  // devolve a janela de 90 dias, então filtrar aqui nunca acharia quem venceu
  // antes disso — que foi justamente o caso que motivou este campo.
  // Debounce de 300ms pra não disparar uma consulta por tecla.
  const [buscaInput, setBuscaInput] = useState('');
  const [busca, setBusca] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaInput.trim()), 300);
    return () => clearTimeout(t);
  }, [buscaInput]);

  const { data: kpis } = useQuery({
    queryKey: ['monetizacao-kpis', showSandbox],
    queryFn: async () => (await api.get(`/admin/monetizacao/kpis${qs}`)).data?.data,
  });

  const { data: subs, isLoading } = useQuery({
    queryKey: ['monetizacao-pro-subscribers', showSandbox, busca],
    queryFn: async () => {
      const { data } = await api.get('/admin/monetizacao/pro/subscribers', {
        params: {
          ...(showSandbox ? { include_sandbox: 'true' } : {}),
          ...(busca ? { search: busca } : {}),
        },
      });
      return Array.isArray(data?.data) ? data.data : [];
    },
  });

  const rawItems: any[] = subs ?? [];
  const { sorted: items, sort, handleSort } = useTableSort(rawItems, 'started_at', 'desc', (row, field) => {
    if (field === 'user') return row.display_name || row.username;
    if (field === 'price_brl_cents') return row.price_brl_cents ?? 0;
    return row[field];
  });

  return (
    <Layout title="Amuzze Pro">
      <div className="mb-6 flex items-start justify-between gap-4">
        <p className="text-sm text-text-muted">
          Visão completa da assinatura Pro: KPIs de uso + lista detalhada de assinantes. Pra receita e finanças, veja <span className="text-brand">Monetização</span> e <span className="text-brand">Finanças</span>.
        </p>
        <button
          type="button"
          onClick={toggleSandbox}
          className={`flex shrink-0 items-center gap-2 rounded-lg border px-3 py-1.5 text-xs transition-colors ${
            showSandbox
              ? 'border-amber-800 bg-amber-950/60 text-amber-200 hover:bg-amber-900/60'
              : 'border-border bg-bg-input text-text-muted hover:text-text-primary'
          }`}
          title="Assinaturas de teste (Sandbox Tester Apple/Google) — ocultas por default."
        >
          <FlaskConical size={14} />
          {showSandbox ? 'Sandbox ativo' : 'Mostrar sandbox'}
        </button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Card>
          <CardHeader>
            <CardTitle>Pro ativos</CardTitle>
            <Sparkles size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(kpis?.pro_subscribers_active)}</CardValue>
          <CardSubtitle>assinaturas em vigor</CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Novos (30d)</CardTitle>
            <TrendingUp size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(kpis?.pro_subscribers_new_30d)}</CardValue>
          <CardSubtitle>conversões no último mês</CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cancelados (30d)</CardTitle>
            <UserMinus size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(kpis?.pro_subscribers_cancelled_30d)}</CardValue>
          <CardSubtitle>último mês</CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>MRR líquido</CardTitle>
            <DollarSign size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatBrl(kpis?.mrr_brl_net)}</CardValue>
          <CardSubtitle>já descontadas comissão e impostos da loja</CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Conversion %</CardTitle>
            <Percent size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{kpis?.conversion_pct ?? 0}%</CardValue>
          <CardSubtitle>do MAU virou Pro</CardSubtitle>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Churn % 30d</CardTitle>
            <TrendingDown size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{kpis?.churn_pct_30d ?? 0}%</CardValue>
          <CardSubtitle>perda mensal</CardSubtitle>
        </Card>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-text-secondary">
          {busca
            ? 'Assinantes — busca em todo o histórico'
            : 'Assinantes (vigentes + encerrados nos últimos 90d)'}
        </h2>
        <div className="relative min-w-[240px]">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            className="pl-9"
            placeholder="Buscar por username ou nome…"
            value={buscaInput}
            onChange={(e) => setBuscaInput(e.target.value)}
          />
        </div>
      </div>

      {busca && (
        <p className="mb-3 text-xs text-text-muted">
          A busca ignora a janela de 90 dias — inclui assinaturas antigas que não aparecem na lista padrão.
        </p>
      )}

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-bg-input text-left text-xs uppercase text-text-secondary">
            <tr>
              <SortableColumn label="User" field="user" sort={sort} onSort={handleSort} />
              <SortableColumn label="Plataforma" field="source" sort={sort} onSort={handleSort} />
              <SortableColumn label="Plano" field="plan_code" sort={sort} onSort={handleSort} />
              <SortableColumn label="Status" field="status" sort={sort} onSort={handleSort} />
              <SortableColumn label="Preço" field="price_brl_cents" sort={sort} onSort={handleSort} align="right" />
              <SortableColumn label="Início" field="started_at" sort={sort} onSort={handleSort} />
              <SortableColumn label="Termina em" field="ends_at" sort={sort} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="p-8 text-center text-text-muted">Carregando…</td></tr>
            )}
            {items.map((s) => (
              <tr key={s.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text-primary">{s.display_name || s.username}</span>
                    {s.environment === 'sandbox' && (
                      <span className="rounded bg-amber-950/60 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-300" title="Assinatura de teste (Sandbox Tester)">
                        sandbox
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-text-muted">@{s.username} · {s.profile_type}</div>
                </td>
                <td className="px-4 py-3 text-text-secondary">{SOURCE_LABEL[s.source] || s.source}</td>
                <td className="px-4 py-3 text-text-secondary" title={s.plan_code}>{planLabel(s.plan_code)}</td>
                <td className="px-4 py-3">
                  {/* `status` sozinho não basta: a row fica 'active' até a loja
                      avisar que venceu, e esse aviso pode nunca chegar. Sem o
                      `vigente` do servidor, uma assinatura morta há um mês
                      aparecia aqui em verde como se estivesse pagando. */}
                  {s.status === 'active' && !s.vigente && (
                    <span
                      className="rounded-md bg-red-950 px-2 py-0.5 text-xs text-red-300"
                      title="Passou do ends_at mas continua marcada como ativa: a loja não enviou (ou não processamos) o evento de renovação/expiração. Conferir no RevenueCat."
                    >
                      vencido · loja não avisou
                    </span>
                  )}
                  {s.status === 'active' && s.vigente && !s.cancelled_at && (
                    <span className="rounded-md bg-emerald-950 px-2 py-0.5 text-xs text-emerald-300">ativo</span>
                  )}
                  {s.status === 'active' && s.vigente && s.cancelled_at && (
                    <span className="rounded-md bg-amber-950 px-2 py-0.5 text-xs text-amber-300">cancelado, ativo até fim</span>
                  )}
                  {s.status === 'cancelled' && (
                    <span className="rounded-md bg-red-950 px-2 py-0.5 text-xs text-red-300">cancelado</span>
                  )}
                  {s.status === 'expired' && (
                    <span className="rounded-md bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300">expirado</span>
                  )}
                  {s.status === 'paused' && (
                    <span className="rounded-md bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300">pausado</span>
                  )}
                  {s.status === 'in_grace' && (
                    <span className="rounded-md bg-amber-950 px-2 py-0.5 text-xs text-amber-300">em carência</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-text-secondary">{formatBrl((s.price_brl_cents || 0) / 100)}</td>
                <td className="px-4 py-3 text-text-secondary">{formatDate(s.started_at)}</td>
                <td className="px-4 py-3 text-text-secondary">{formatDate(s.ends_at)}</td>
              </tr>
            ))}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-text-muted">
                  {busca ? `Nenhum assinante para "${busca}".` : 'Nenhum assinante ainda.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </Layout>
  );
}
